import Layout from "../components/Layout"
import BannerGallery from "../components/BannerGallery"
import NewsSection from "../components/NewsSection"
import Alert from "../components/Alert"
import { useState, useEffect, useRef } from "react";
import { Search, X, Package, CheckCircle, XCircle, Clock, TrendingUp, Zap, Star } from "react-feather"

const Dashboard = () => {
  const [medicineCount, setMedicineCount] = useState(0)
  const [productCount, setProductCount] = useState(0)
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [alert, setAlert] = useState(null)
  const API_BASE_URL = 'https://api.tenarx.net';
  
  // All medicines data for client-side search
  const [allMedicines, setAllMedicines] = useState([])
  
  // Unified search state management
  const [searchState, setSearchState] = useState({
    term: '',
    suggestions: [],
    results: [],
    isLoading: false,
    isOpen: false,
    mode: 'idle', // 'idle' | 'suggestions' | 'results' | 'loading'
    didYouMean: null,
    smartSuggestions: []
  })
  
  const [updatingAvailability, setUpdatingAvailability] = useState({})
  const [recentSearches, setRecentSearches] = useState([])
  const [popularSearches, setPopularSearches] = useState([])
  const searchRef = useRef(null)

  // Get pharmacyId from token
  const getPharmacyId = () => {
    try {
      const token = localStorage.getItem("userToken")
      if (!token) return null
      const payload = JSON.parse(atob(token.split(".")[1]))
      return payload.userId
    } catch {
      return null
    }
  }
  const pharmacyId = getPharmacyId()

  // Fuzzy search function with spelling tolerance
  const fuzzySearch = (searchTerm, medicines) => {
    if (!searchTerm.trim()) return []
    
    const term = searchTerm.toLowerCase()
    const results = []
    
    medicines.forEach(medicine => {
      let score = 0
      let matched = false
      
      // Check medicine name (highest priority)
      if (medicine.name.toLowerCase().includes(term)) {
        score += 100
        matched = true
      } else if (medicine.name.toLowerCase().startsWith(term)) {
        score += 80
        matched = true
      } else if (medicine.name.toLowerCase().endsWith(term)) {
        score += 60
        matched = true
      }
      
      // Check content/description
      if (medicine.content.toLowerCase().includes(term)) {
        score += 40
        matched = true
      }
      
      // Check type
      if (medicine.type.toLowerCase().includes(term)) {
        score += 30
        matched = true
      }
      
      // Check country
      if (medicine.country.toLowerCase().includes(term)) {
        score += 20
        matched = true
      }
      
      // Fuzzy matching for typos (spelling tolerance)
      if (!matched) {
        const nameWords = medicine.name.toLowerCase().split(' ')
        const termWords = term.split(' ')
        
        termWords.forEach(termWord => {
          nameWords.forEach(nameWord => {
            // Check if words are similar (typo tolerance)
            if (nameWord.length > 2 && termWord.length > 2) {
              const similarity = calculateSimilarity(termWord, nameWord)
              if (similarity > 0.7) { // 70% similarity threshold
                score += similarity * 50
                matched = true
              }
            }
          })
        })
      }
      
      if (matched && score > 10) {
        results.push({ ...medicine, searchScore: score })
      }
    })
    
    // Sort by relevance score
    return results.sort((a, b) => b.searchScore - a.searchScore)
  }

  // Calculate similarity between two strings (for typo tolerance)
  const calculateSimilarity = (str1, str2) => {
    if (str1 === str2) return 1
    
    const longer = str1.length > str2.length ? str1 : str2
    const shorter = str1.length > str2.length ? str2 : str1
    
    if (longer.length === 0) return 1
    
    const editDistance = levenshteinDistance(longer, shorter)
    return (longer.length - editDistance) / longer.length
  }

  // Levenshtein distance for typo detection
  const levenshteinDistance = (str1, str2) => {
    const matrix = []
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i]
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1]
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          )
        }
      }
    }
    
    return matrix[str2.length][str1.length]
  }

  // Generate "Did You Mean" suggestions
  const generateDidYouMean = (searchTerm, medicines) => {
    if (!searchTerm.trim() || searchTerm.length < 3) return null
    
    const term = searchTerm.toLowerCase()
    const suggestions = []
    
    // Find similar medicine names
    medicines.forEach(medicine => {
      const name = medicine.name.toLowerCase()
      if (name.length > 2 && name !== term) {
        const similarity = calculateSimilarity(term, name)
        if (similarity > 0.6) { // 60% similarity for "Did You Mean"
          suggestions.push({
            text: medicine.name,
            similarity: similarity,
            type: 'didYouMean'
          })
        }
      }
    })
    
    // Find similar medicine types
    const medicineTypes = ['Tablet', 'Capsule', 'Syrup', 'Injection', 'Cream', 'Ointment', 'Drops']
    medicineTypes.forEach(type => {
      const typeLower = type.toLowerCase()
      if (typeLower !== term && typeLower.length > 2) {
        const similarity = calculateSimilarity(term, typeLower)
        if (similarity > 0.6) {
          suggestions.push({
            text: type,
            similarity: similarity,
            type: 'didYouMean'
          })
        }
      }
    })
    
    // Return the best suggestion
    if (suggestions.length > 0) {
      const bestSuggestion = suggestions.sort((a, b) => b.similarity - a.similarity)[0]
      return bestSuggestion
    }
    
    return null
  }

  // Generate smart suggestions with context awareness
  const generateSmartSuggestions = (term) => {
    if (!term.trim()) return []
    
    const suggestions = []
    const termLower = term.toLowerCase()
    
    // 1. Recent searches that match
    recentSearches.forEach(search => {
      if (search.toLowerCase().includes(termLower) && !suggestions.some(s => s.text === search)) {
        suggestions.push({ 
          type: 'recent', 
          text: search, 
          icon: Clock,
          priority: 90
        })
      }
    })
    
    // 2. Popular searches that match
    popularSearches.forEach(search => {
      if (search.toLowerCase().includes(termLower) && !suggestions.some(s => s.text === search)) {
        suggestions.push({ 
          type: 'popular', 
          text: search, 
          icon: TrendingUp,
          priority: 85
        })
      }
    })
    
    // 3. Medicine names that start with the term
    allMedicines.forEach(medicine => {
      const name = medicine.name.toLowerCase()
      if (name.startsWith(termLower) && !suggestions.some(s => s.text === medicine.name)) {
        suggestions.push({ 
          type: 'medicine', 
          text: medicine.name, 
          icon: Package,
          priority: 95,
          medicine: medicine
        })
      }
    })
    
    // 4. Medicine types that match
    const medicineTypes = ['Tablet', 'Capsule', 'Syrup', 'Injection', 'Cream', 'Ointment', 'Drops']
    medicineTypes.forEach(type => {
      const typeLower = type.toLowerCase()
      if (typeLower.includes(termLower) && !suggestions.some(s => s.text === type)) {
        suggestions.push({ 
          type: 'category', 
          text: type, 
          icon: Zap,
          priority: 80
        })
      }
    })
    
    // 5. Common medicine categories
    const categories = ['Pain Relief', 'Antibiotics', 'Vitamins', 'Antihistamines', 'Antacids']
    categories.forEach(category => {
      const categoryLower = category.toLowerCase()
      if (categoryLower.includes(termLower) && !suggestions.some(s => s.text === category)) {
        suggestions.push({ 
          type: 'category', 
          text: category, 
          icon: Star,
          priority: 75
        })
      }
    })
    
    // Sort by priority and return top suggestions
    return suggestions
      .sort((a, b) => b.priority - a.priority)
      .slice(0, 10)
  }

  // Client-side search function with smart features
  const performClientSideSearch = (searchTerm) => {
    if (!searchTerm.trim()) {
      setSearchState(prev => ({
        ...prev,
        results: [],
        didYouMean: null,
        mode: 'idle',
        isOpen: false
      }))
      return
    }

    // Perform fuzzy search with spelling tolerance
    const fuzzyResults = fuzzySearch(searchTerm, allMedicines)
    
    // Generate "Did You Mean" suggestion
    const didYouMean = generateDidYouMean(searchTerm, allMedicines)
    
    setSearchState(prev => ({
      ...prev,
      results: fuzzyResults,
      didYouMean: didYouMean,
      mode: 'results',
      isOpen: true
    }))


  }



  // Handle search input changes with smart suggestions
  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchState(prev => ({ ...prev, term: value }))
    
    if (value.trim().length >= 2) {
      // Generate smart suggestions
      const smartSuggestions = generateSmartSuggestions(value)
      setSearchState(prev => ({
        ...prev,
        smartSuggestions: smartSuggestions,
        mode: 'suggestions',
        isOpen: true,
        results: [],
        didYouMean: null
      }))
      
      // Perform client-side search immediately
      performClientSideSearch(value)
    } else {
      setSearchState(prev => ({
        ...prev,
        smartSuggestions: [],
        suggestions: [],
        results: [],
        didYouMean: null,
        mode: 'idle',
        isOpen: false
      }))
    }
  }

  // Handle search form submission
  const handleSearch = async (e) => {
    e.preventDefault()
    if (!searchState.term.trim()) return
    performClientSideSearch(searchState.term)
  }

  // Clear search
  const clearSearch = () => {
    setSearchState({
      term: '',
      suggestions: [],
      smartSuggestions: [],
      results: [],
      didYouMean: null,
      isLoading: false,
      isOpen: false,
      mode: 'idle'
    })
  }

  // Select a suggestion
  const selectSuggestion = (suggestion) => {
    setSearchState(prev => ({
      ...prev,
      term: suggestion.text,
      mode: 'suggestions',
      isOpen: true,
      smartSuggestions: [],
      results: [],
      didYouMean: null
    }))
    performClientSideSearch(suggestion.text)
  }

  // Use "Did You Mean" suggestion
  const handleDidYouMean = (suggestion) => {
    setSearchState(prev => ({
      ...prev,
      term: suggestion.text,
      mode: 'loading',
      isOpen: true,
      smartSuggestions: [],
      results: [],
      didYouMean: null
    }))
    performClientSideSearch(suggestion.text)
  }

  // Close search dropdown
  const closeSearch = () => {
    setSearchState(prev => ({
      ...prev,
      isOpen: false
    }))
  }

  // Handle click outside search
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        closeSearch()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && searchState.isOpen) {
        closeSearch()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [searchState.isOpen])

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)
      try {
        const token = localStorage.getItem("userToken")
        // Fetch medicines for this pharmacy
        const medRes = await fetch(`${API_BASE_URL}/api/medicines/my`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const medData = await medRes.json()
        let medicines = []
        if (medRes.ok && medData.success) {
          medicines = medData.data || []
          setMedicineCount(medicines.length)
          // Store all medicines for client-side search
          setAllMedicines(medicines)
          
          // Generate popular searches from medicine names and types
          const names = medicines.map(m => m.name).slice(0, 10)
          const types = [...new Set(medicines.map(m => m.type))].slice(0, 5)
          setPopularSearches([...names, ...types])
        } else {
          setMedicineCount(0)
        }
        // Fetch products for this pharmacy
        const prodRes = await fetch(`${API_BASE_URL}/api/products`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const prodData = await prodRes.json()
        let products = []
        if (prodRes.ok && prodData.success) {
          products = (prodData.data || []).filter((p) => p.pharmacyId === pharmacyId)
          setProductCount(products.length)
        } else {
          setProductCount(0)
        }
        // Build activities (last 5, most recent first)
        const medActs = medicines.map((m) => ({
          type: "medicine",
          name: m.name,
          time: m.createdAt ? new Date(m.createdAt) : null,
        }))
        const prodActs = products.map((p) => ({
          type: "product",
          name: p.name,
          time: p.createdAt ? new Date(p.createdAt) : null,
        }))
        const allActs = [...medActs, ...prodActs]
          .filter((a) => a.time)
          .sort((a, b) => b.time - a.time)
          .slice(0, 5)
        setActivities(allActs)
      } catch (err) {
        setError("Failed to load dashboard data")
      } finally {
        setLoading(false)
      }
    }
    fetchData()
    // eslint-disable-next-line
  }, [])

  const formatTimeAgo = (date) => {
    if (!date) return ""
    const now = new Date()
    const diff = Math.floor((now - date) / 1000)
    if (diff < 60) return `${diff}s ago`
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
    return `${Math.floor(diff / 86400)}d ago`
  }



  const toggleAvailability = async (medicineId, currentStatus) => {
    if (updatingAvailability[medicineId]) return // Prevent multiple clicks

    setUpdatingAvailability(prev => ({ ...prev, [medicineId]: true }))
    setAlert(null) // Clear any previous alerts

    try {
      const token = localStorage.getItem("userToken")
      if (!token) {
        throw new Error("Authentication token not found")
      }

      const response = await fetch(`${API_BASE_URL}/api/medicines/toggle-status/${medicineId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      const data = await response.json()
      
      if (response.ok && data.success) {
        // Update the search results with new availability status
        setSearchState(prev => ({
          ...prev,
          results: prev.results.map(medicine => 
            medicine._id === medicineId 
              ? { ...medicine, isAvailable: !currentStatus }
              : medicine
          )
        }))
        
        // Update the allMedicines array as well
        setAllMedicines(prev => 
          prev.map(medicine => 
            medicine._id === medicineId 
              ? { ...medicine, isAvailable: !currentStatus }
              : medicine
          )
        )
        
        // Update medicine count if needed
        if (currentStatus) {
          setMedicineCount(prev => prev - 1)
        } else {
          setMedicineCount(prev => prev + 1)
        }

        // Show success alert
        setAlert({ 
          type: "success", 
          message: `Medicine marked as ${!currentStatus ? "available" : "sold out"} successfully` 
        })
      } else {
        // Show error alert from API response
        setAlert({ 
          type: "error", 
          message: data.message || "Failed to toggle availability" 
        })
      }
    } catch (error) {
      console.error("Error toggling availability:", error)
      // Show generic error alert
      setAlert({ 
        type: "error", 
        message: "Failed to toggle availability. Please try again." 
      })
    } finally {
      setUpdatingAvailability(prev => ({ ...prev, [medicineId]: false }))
    }
  }

  return (
    <Layout>
      {alert && (
        <Alert type={alert.type} dismissible onDismiss={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}
      
      <div className="space-y-6">
        <div className="rounded-xl bg-gradient-to-r from-blue-50 via-blue-100 to-indigo-50 border border-blue-100 p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">Pharmacy Dashboard</h1>
              <p className="text-sm text-gray-600 mt-1">Manage your medicines and products efficiently</p>
            </div>
            <div className="flex gap-2 text-xs">
              <span className="px-3 py-1 rounded-full bg-white border border-blue-200 text-blue-700">Medicines: {loading ? '-' : medicineCount}</span>
              <span className="px-3 py-1 rounded-full bg-white border border-indigo-200 text-indigo-700">Products: {loading ? '-' : productCount}</span>
            </div>
          </div>
        </div>
        {/* Search Bar */}
        <div className="w-full relative" ref={searchRef}>
          <form onSubmit={handleSearch} className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search medicines with smart suggestions..."
                value={searchState.term}
                onChange={handleSearchChange}
                onFocus={() => {
                  if (searchState.term.trim().length >= 2) {
                    setSearchState(prev => ({ ...prev, isOpen: true }))
                  }
                }}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white"
              />
              {searchState.term && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </form>

          {/* Backdrop for search overlay */}
          {searchState.isOpen && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-20 z-[90]"
              onClick={closeSearch}
            />
          )}

          {/* Unified Search Dropdown */}
          {searchState.isOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl z-[100] max-h-96 overflow-hidden transform transition-all duration-200 ease-out">
              {/* Smart Suggestions */}
              {searchState.mode === 'suggestions' && searchState.smartSuggestions.length > 0 && (
                <div className="py-3">
                  <div className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wide border-b border-gray-100 bg-blue-50">
                    💡 Smart Suggestions
                  </div>
                  {searchState.smartSuggestions.map((suggestion, index) => (
                    <button
                      key={`${suggestion.type}-${index}`}
                      onClick={() => selectSuggestion(suggestion)}
                      className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 group"
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${
                          suggestion.type === 'recent' ? 'bg-blue-100 text-blue-600' :
                          suggestion.type === 'popular' ? 'bg-green-100 text-green-600' :
                          suggestion.type === 'medicine' ? 'bg-purple-100 text-purple-600' :
                          'bg-orange-100 text-orange-600'
                        } group-hover:scale-110 transition-transform duration-200`}>
                          <suggestion.icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                            {suggestion.text}
                          </div>
                          <div className="text-xs text-gray-500 capitalize">
                            {suggestion.type === 'recent' ? 'Recent search' :
                             suggestion.type === 'popular' ? 'Popular search' :
                             suggestion.type === 'medicine' ? 'Medicine name' :
                             'Category'}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Search Results with "Did You Mean" */}
              {searchState.mode === 'results' && (
                <div className="py-3">
                  {/* "Did You Mean" Section */}
                  {searchState.didYouMean && (
                    <div className="px-4 py-3 bg-yellow-50 border-b border-yellow-200">
                      <div className="flex items-center space-x-3">
                        <Star className="w-5 h-5 text-yellow-600" />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-yellow-800">
                            Did you mean: <span className="font-semibold">{searchState.didYouMean.text}</span>?
                          </div>
                          <div className="text-xs text-yellow-600 mt-1">
                            Click to search for "{searchState.didYouMean.text}"
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setSearchState(prev => ({
                              ...prev,
                              term: searchState.didYouMean.text,
                              mode: 'loading',
                              isOpen: true,
                              smartSuggestions: [],
                              results: [],
                              didYouMean: null
                            }))
                            performClientSideSearch(searchState.didYouMean.text)
                          }}
                          className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full hover:bg-yellow-200 transition-colors"
                        >
                          Use This
                        </button>
                      </div>
                    </div>
                  )}

                  {searchState.results.length > 0 ? (
                    <>
                      <div className="px-4 py-2 text-xs font-semibold text-gray-600 uppercase tracking-wide border-b border-gray-100 bg-gray-50">
                        Search Results ({searchState.results.length})
                      </div>
                      {searchState.results.map((medicine) => (
                        <div
                          key={medicine._id}
                          className="px-4 py-4 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <Package className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-gray-900 truncate">{medicine.name}</div>
                              <div className="text-sm text-gray-500 mt-1">
                                {medicine.type} • {medicine.measurementUnit} • {medicine.country}
                              </div>
                              {medicine.searchScore && (
                                <div className="text-xs text-blue-600 mt-1">
                                  Relevance: {Math.round(medicine.searchScore)}%
                                </div>
                              )}
                            </div>
                            
                            {/* Availability Toggle Button */}
                            <button
                              onClick={() => toggleAvailability(medicine._id, medicine.isAvailable)}
                              disabled={updatingAvailability[medicine._id]}
                              className={`flex items-center space-x-2 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 ${
                                medicine.isAvailable
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200 hover:shadow-md'
                                  : 'bg-red-100 text-red-800 hover:bg-red-200 hover:shadow-md'
                              } ${updatingAvailability[medicine._id] ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}`}
                            >
                              {updatingAvailability[medicine._id] ? (
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                              ) : medicine.isAvailable ? (
                                <>
                                  <CheckCircle className="w-3 h-3" />
                                  <span>Available</span>
                                </>
                              ) : (
                                <>
                                  <XCircle className="w-3 h-3" />
                                  <span>Out of Stock</span>
                                </>
                              )}
                            </button>

                            {/* Stock Status Badge */}
                            <div className={`px-3 py-1 text-xs font-medium rounded-full ${
                              medicine.isAvailable 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {medicine.isAvailable ? 'In Stock' : 'Out of Stock'}
                            </div>
                          </div>
                          
                          {/* Action Hint */}
                          <div className="mt-3 text-xs text-gray-400 text-center bg-gray-50 px-3 py-2 rounded-lg">
                            💡 Click availability button to toggle • Click anywhere else to view details
                          </div>
                        </div>
                      ))}
                    </>
                  ) : searchState.term.trim() && (
                    <div className="p-8 text-center text-gray-500">
                      <div className="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                        <Package className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-sm font-medium mb-2">No medicines found</p>
                      <p className="text-xs text-gray-400">Try searching with different keywords</p>
                      {searchState.didYouMean && (
                        <div className="mt-3">
                                                  <button
                          onClick={() => handleDidYouMean(searchState.didYouMean)}
                          className="px-4 py-2 bg-blue-100 text-blue-800 text-sm font-medium rounded-lg hover:bg-blue-200 transition-colors"
                        >
                          Did you mean: {searchState.didYouMean.text}?
                        </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <BannerGallery />
        
        
        {/* Content Grid - Updated to 2 columns since we removed Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Activities */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h3>
            {loading ? (
              <div className="text-gray-500">Loading...</div>
            ) : error ? (
              <div className="text-red-500">{error}</div>
            ) : activities.length === 0 ? (
              <div className="text-gray-500">No recent activities.</div>
            ) : (
            <div className="space-y-4">
                {activities.map((activity, index) => (
                <div key={index} className="flex items-center space-x-3">
                    <div className={`w-2 h-2 rounded-full ${activity.type === "medicine" ? "bg-blue-500" : "bg-green-500"}`}></div>
                  <div className="flex-1">
                      <p className="text-sm text-gray-900">
                        {activity.type === "medicine" ? "Medicine added" : "Product added"}
                      </p>
                      <p className="text-sm text-gray-500">{activity.name}</p>
                  </div>
                    <span className="text-xs text-gray-400">{formatTimeAgo(activity.time)}</span>
                </div>
              ))}
            </div>
            )}
          </div>
          
          {/* News Section */}
          <NewsSection />
        </div>
      </div>
    </Layout>
  )
}

export default Dashboard;
